import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  InputBase,
  ButtonBase,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogContent,
  Checkbox,
  FormControlLabel,
  Popover,
} from "@mui/material";
import { admin_base_url } from "../../Global";
import { useAuth } from "../../context/AuthContext";
import usePermission from "../../hooks/usePermission";
import {
  ROLES,
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_PERMISSIONS,
  PERMISSIONS,
  UI_PERMISSIONS,
  PERMISSION_LABELS,
  SELECTABLE_ROLES,
} from "../../adminConfig";

const STATUS_STYLES = {
  active: { bg: "rgba(22,163,74,0.15)", color: "#4ade80", label: "Active" },
  pending: { bg: "rgba(234,179,8,0.15)", color: "#facc15", label: "Pending" },
  revoked: { bg: "rgba(239,68,68,0.15)", color: "#f87171", label: "Revoked" },
  blocked: { bg: "rgba(239,68,68,0.12)", color: "#fb923c", label: "Blocked" },
};

const EMPTY_CREATE = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  permissions: [],
};

const AdminManagement = () => {
  const { isSuperAdmin, user: currentUser } = usePermission();
  const { refreshUser } = useAuth();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [permAnchor, setPermAnchor] = useState(null);

  const token = localStorage.getItem("accessToken");

  const fetchAdmins = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(admin_base_url + "/all", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ superAdminId: currentUser?._id }),
      });
      const data = await res.json();
      setAdmins(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, currentUser?._id]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);



  const updateStatus = async (admin, status) => {
    setActionLoading(true);
    setConfirmDialog(null);

    const targetAdminId = admin._id;
    const targetRole = admin.role;
    const currentPerms = Array.isArray(admin.permissions)
      ? admin.permissions
      : [];
    const rolePermissions = ROLE_PERMISSIONS[targetRole] || [];

    try {
      if (status === "active") {
        const additions = rolePermissions.filter(
          (p) => !currentPerms.includes(p),
        );
        if (additions.length > 0) {
          await fetch(admin_base_url + "/approve-permission", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              adminId: currentUser?._id,
              targetAdminId: targetAdminId,
              permissions: additions,
            }),
          });
        }
      } else if (status === "revoked") {
        if (currentPerms.length > 0) {
          await fetch(admin_base_url + "/revoke-permission", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              adminId: currentUser?._id,
              targetAdminId: targetAdminId,
              permissions: currentPerms,
            }),
          });
        }
      } else if (status === "blocked") {
        await fetch(admin_base_url + "/block-admin", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            adminId: currentUser?._id,
            targetAdminId: targetAdminId,
          }),
        });
      } else if (status === "unblocked") {
        await fetch(admin_base_url + "/unblock-admin", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            adminId: currentUser?._id,
            targetAdminId: targetAdminId,
          }),
        });
      }
      await Promise.all([fetchAdmins(), refreshUser()]);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteAdmin = async (targetAdminId) => {
    setActionLoading(true);
    setConfirmDialog(null);
    try {
      await fetch(admin_base_url + "/delete-admin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          adminId: currentUser?._id,
          targetAdminId: targetAdminId,
        }),
      });
      await Promise.all([fetchAdmins(), refreshUser()]);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };


  const togglePermissionImmediate = async (admin, perm, currentPerms) => {
    const has = currentPerms.includes(perm);
    const endpoint = has ? "/revoke-permission" : "/approve-permission";
    const nextPerms = has ? currentPerms.filter(p => p !== perm) : [...currentPerms, perm];

    setAdmins(prev => prev.map(a => a._id === admin._id ? { ...a, permissions: nextPerms } : a));
    setPermAnchor(prev => prev ? { ...prev, selected: nextPerms } : prev);

    try {
      const res = await fetch(admin_base_url + endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ adminId: currentUser?._id, targetAdminId: admin._id, permissions: [perm] }),
      });
      if (!res.ok) {
        console.error("Permission save failed:", await res.json().catch(() => ({})));
        fetchAdmins(true);
      }
    } catch (err) {
      console.error(err);
      fetchAdmins(true);
    }
  };

  const selectAllPermissionsImmediate = async (admin, currentPerms) => {
    const additions = UI_PERMISSIONS.filter(p => !currentPerms.includes(p));
    if (additions.length === 0) return;

    const nextPerms = [...new Set([...currentPerms, ...UI_PERMISSIONS])];

    setAdmins(prev => prev.map(a => a._id === admin._id ? { ...a, permissions: nextPerms } : a));
    setPermAnchor(prev => prev ? { ...prev, selected: nextPerms } : prev);

    try {
      const res = await fetch(admin_base_url + "/approve-permission", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ adminId: currentUser?._id, targetAdminId: admin._id, permissions: additions }),
      });
      if (!res.ok) {
        console.error("Give All Access failed:", await res.json().catch(() => ({})));
        fetchAdmins(true);
      }
    } catch (err) {
      console.error(err);
      fetchAdmins(true);
    }
  };

  const handleCreateAdmin = async () => {
    setCreateError("");
    if (
      !createForm.first_name.trim() ||
      !createForm.last_name.trim() ||
      !createForm.email.trim() ||
      !createForm.password.trim()
    ) {
      setCreateError("All fields are required.");
      return;
    }
    setCreateLoading(true);
    try {
      const res = await fetch(admin_base_url + "/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          first_name: createForm.first_name.trim(),
          last_name: createForm.last_name.trim(),
          email: createForm.email.trim(),
          password: createForm.password,
          role_type: "CUSTOM",
          permissions: createForm.permissions,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setCreateError(data.message || "Failed to create admin.");
        return;
      }
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      await fetchAdmins();
    } catch (err) {
      setCreateError("Network error. Please try again.");
    } finally {
      setCreateLoading(false);
    }
  };

  const filtered = admins.filter((a) => {
    const matchSearch =
      !search ||
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === "all" || a.role === filterRole;
    const effectiveStatus = a.isBlockedBySuperAdmin ? "blocked" : a.status;
    const matchStatus =
      filterStatus === "all" || effectiveStatus === filterStatus;
    return matchSearch && matchRole && matchStatus;
  });

  if (!isSuperAdmin) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          gap: "16px",
        }}
      >
        <Typography
          style={{
            color: "#9ca3af",
            fontFamily: "Gilroy, sans-serif",
            fontSize: "16px",
          }}
        >
          Only the Super Admin can manage admin accounts.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      style={{
        padding: "32px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <Box>
          <Typography
            style={{
              color: "white",
              fontSize: "24px",
              fontWeight: 700,
              fontFamily: "Gilroy, sans-serif",
            }}
          >
            Admin Management
          </Typography>
          <Typography
            style={{
              color: "#6b7280",
              fontSize: "13px",
              fontFamily: "Gilroy, sans-serif",
              marginTop: "4px",
            }}
          >
            {admins.length} total admin accounts &nbsp;·&nbsp;
            {admins.filter((a) => a.status === "pending").length} pending
            approval
          </Typography>
        </Box>

        <ButtonBase
          onClick={() => {
            setCreateOpen(true);
            setCreateError("");
            setCreateForm(EMPTY_CREATE);
          }}
          style={{
            background: "#FF6B35",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            fontFamily: "Gilroy, sans-serif",
            fontSize: "13px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          + Create Admin
        </ButtonBase>
      </Box>

      <Box style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          sx={{
            background: "#1f2937",
            borderRadius: "8px",
            padding: "0 16px",
            height: "40px",
            color: "white",
            fontFamily: "Gilroy, sans-serif",
            fontSize: "13px",
            flex: 1,
            minWidth: "200px",
            border: "1px solid #374151",
          }}
        />

        <Select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          variant="standard"
          disableUnderline
          sx={{
            background: "#1f2937",
            borderRadius: "8px",
            padding: "0 12px",
            height: "40px",
            color: "#9ca3af",
            fontFamily: "Gilroy, sans-serif",
            fontSize: "13px",
            minWidth: "170px",
            border: "1px solid #374151",
          }}
        >
          <MenuItem value="all">All Roles</MenuItem>
          {Object.values(ROLES).map((r) => (
            <MenuItem key={r} value={r} sx={{ color: ROLE_COLORS[r] }}>
              {ROLE_LABELS[r]}
            </MenuItem>
          ))}
        </Select>

        <Select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          variant="standard"
          disableUnderline
          sx={{
            background: "#1f2937",
            borderRadius: "8px",
            padding: "0 12px",
            height: "40px",
            color: "#9ca3af",
            fontFamily: "Gilroy, sans-serif",
            fontSize: "13px",
            minWidth: "130px",
            border: "1px solid #374151",
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="revoked">Revoked</MenuItem>
          <MenuItem value="blocked">Blocked</MenuItem>
        </Select>
      </Box>

      {loading ? (
        <Box
          style={{ display: "flex", justifyContent: "center", padding: "64px" }}
        >
          <CircularProgress sx={{ color: "#FF6B35" }} />
        </Box>
      ) : (
        <Box
          style={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #1f2937",
          }}
        >
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 2fr 3fr 1fr 2fr",
              background: "#111827",
              padding: "12px 20px",
            }}
          >
            {["Name", "Email", "Permissions", "Status", "Actions"].map((h) => (
              <Typography
                key={h}
                style={{
                  color: "#6b7280",
                  fontSize: "11px",
                  fontWeight: 700,
                  fontFamily: "Gilroy, sans-serif",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {filtered.length === 0 ? (
            <Box
              style={{
                background: "#0f172a",
                padding: "40px",
                textAlign: "center",
              }}
            >
              <Typography
                style={{ color: "#6b7280", fontFamily: "Gilroy, sans-serif" }}
              >
                No admins found.
              </Typography>
            </Box>
          ) : (
            filtered.map((admin, i) => {
              const isSelf = admin._id === currentUser?._id;
              const isThisSuperAdmin = admin.role === ROLES.SUPER_ADMIN;
              const isBlocked = !!(
                admin.isBlockedBySuperAdmin ||
                admin.isBlocked ||
                admin.blocked
              );
              const statusStyle = isBlocked
                ? STATUS_STYLES.blocked
                : STATUS_STYLES.active;

              return (
                <Box
                  key={admin._id || i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 3fr 1fr 2fr",
                    background: i % 2 === 0 ? "#0f172a" : "#111827",
                    padding: "16px 20px",
                    borderTop: "1px solid #1f2937",
                    alignItems: "center",
                  }}
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Box
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        background: (ROLE_COLORS[admin.role] || "#aaa") + "22",
                        color: ROLE_COLORS[admin.role] || "#aaa",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {(admin.name ||
                        [admin.first_name, admin.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                        admin.username ||
                        admin.email ||
                        "?")[0].toUpperCase()}
                    </Box>
                    <Typography
                      style={{
                        color: "white",
                        fontSize: "14px",
                        fontWeight: 600,
                        fontFamily: "Gilroy, sans-serif",
                      }}
                    >
                      {admin.name ||
                        [admin.first_name, admin.last_name]
                          .filter(Boolean)
                          .join(" ") ||
                        admin.username ||
                        "—"}
                      {isSelf && (
                        <span
                          style={{
                            color: "#6b7280",
                            fontSize: "11px",
                            marginLeft: "6px",
                          }}
                        >
                          (you)
                        </span>
                      )}
                    </Typography>
                  </Box>

                  <Typography
                    style={{
                      color: "#9ca3af",
                      fontSize: "13px",
                      fontFamily: "Gilroy, sans-serif",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      paddingRight: "12px",
                    }}
                  >
                    {admin.email}
                  </Typography>

                  <Box>
                    <ButtonBase
                      onClick={(e) => {
                        const current = Array.isArray(admin.permissions) ? admin.permissions : [];
                        setPermAnchor({ el: e.currentTarget, admin, selected: [...current], initial: [...current] });
                      }}
                      style={{
                        background: "rgba(139,92,246,0.12)",
                        border: "1px solid rgba(139,92,246,0.25)",
                        color: "#a78bfa",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 700,
                        fontFamily: "Gilroy, sans-serif",
                        gap: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      Permissions
                      <span style={{ opacity: 0.7, fontWeight: 400 }}>
                        ({isThisSuperAdmin ? "All" : (admin.permissions || []).filter((p) => UI_PERMISSIONS.includes(p)).length}/{UI_PERMISSIONS.length})
                      </span>
                      <span style={{ fontSize: "9px", marginLeft: "2px" }}>▼</span>
                    </ButtonBase>
                  </Box>

                  <Box
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "4px 10px",
                      borderRadius: "999px",
                      background: statusStyle.bg,
                      color: statusStyle.color,
                      fontSize: "11px",
                      fontWeight: 700,
                      fontFamily: "Gilroy, sans-serif",
                      width: "fit-content",
                    }}
                  >
                    {statusStyle.label}
                  </Box>

                  {(isSelf || isThisSuperAdmin) &&
                    admin.status === "pending" ? (
                    <ActionBtn
                      label="Approve"
                      bg="rgba(22,163,74,0.15)"
                      color="#4ade80"
                      disabled={actionLoading}
                      onClick={() => updateStatus(admin, "active")}
                    />
                  ) : isSelf || isThisSuperAdmin ? (
                    <Typography
                      style={{
                        color: "#374151",
                        fontSize: "12px",
                        fontStyle: "italic",
                      }}
                    >
                      —
                    </Typography>
                  ) : (
                    <Box
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {admin.status === "pending" && !isBlocked && (
                        <ActionBtn
                          label="Approve"
                          bg="rgba(22,163,74,0.15)"
                          color="#4ade80"
                          disabled={actionLoading}
                          onClick={() => updateStatus(admin, "active")}
                        />
                      )}

                      {admin.status === "active" && !isBlocked && (
                        <ActionBtn
                          label="Revoke"
                          bg="rgba(239,68,68,0.15)"
                          color="#f87171"
                          disabled={actionLoading}
                          onClick={() =>
                            setConfirmDialog({
                              admin,
                              type: "revoke",
                              onConfirm: () => updateStatus(admin, "revoked"),
                            })
                          }
                        />
                      )}

                      {admin.status === "revoked" && !isBlocked && (
                        <ActionBtn
                          label="Restore"
                          bg="rgba(59,130,246,0.15)"
                          color="#60a5fa"
                          disabled={actionLoading}
                          onClick={() => updateStatus(admin, "active")}
                        />
                      )}

                      {isBlocked ? (
                        <ActionBtn
                          label="Unblock"
                          bg="rgba(22,163,74,0.12)"
                          color="#86efac"
                          disabled={actionLoading}
                          onClick={() =>
                            setConfirmDialog({
                              admin,
                              type: "unblock",
                              onConfirm: () => updateStatus(admin, "unblocked"),
                            })
                          }
                        />
                      ) : (
                        admin.status !== "pending" && (
                          <ActionBtn
                            label="Block"
                            bg="rgba(251,146,60,0.15)"
                            color="#fb923c"
                            disabled={actionLoading}
                            onClick={() =>
                              setConfirmDialog({
                                admin,
                                type: "block",
                                onConfirm: () => updateStatus(admin, "blocked"),
                              })
                            }
                          />
                        )
                      )}

                      <ActionBtn
                        label="Delete"
                        bg="rgba(127,29,29,0.25)"
                        color="#fca5a5"
                        disabled={actionLoading}
                        onClick={() =>
                          setConfirmDialog({
                            admin,
                            type: "delete",
                            onConfirm: () => deleteAdmin(admin._id),
                          })
                        }
                      />
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>
      )}

      {/* Permissions Popover */}
      <Popover
        open={!!permAnchor}
        anchorEl={permAnchor?.el}
        onClose={() => setPermAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            background: "#1f2937",
            border: "1px solid #374151",
            borderRadius: "10px",
            padding: "12px 16px",
            minWidth: "220px",
          },
        }}
      >
        {permAnchor && (() => {
          const a = permAnchor.admin;
          const isLocked = a.role === ROLES.SUPER_ADMIN;
          const selected = permAnchor.selected || [];
          return (
            <Box style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography style={{ color: "#6b7280", fontSize: "11px", fontFamily: "Gilroy, sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" }}>
                {a.first_name || a.email}'s Permissions
              </Typography>
              {UI_PERMISSIONS.map((perm) => {
                const hasPerm = isLocked || selected.includes(perm);
                return (
                  <FormControlLabel
                    key={perm}
                    control={
                      <Checkbox
                        size="small"
                        checked={hasPerm}
                        disabled={isLocked}
                        onChange={() => togglePermissionImmediate(a, perm, selected)}
                        sx={{
                          color: "#4b5563",
                          padding: "2px 6px",
                          "&.Mui-checked": { color: "#a78bfa" },
                          "&.Mui-disabled": { color: "#6b7280" },
                        }}
                      />
                    }
                    label={
                      <Typography style={{ fontSize: "12px", color: hasPerm ? "#a78bfa" : "#6b7280", fontFamily: "Gilroy, sans-serif", fontWeight: hasPerm ? 600 : 400 }}>
                        {PERMISSION_LABELS[perm] || perm}
                      </Typography>
                    }
                    style={{ margin: 0 }}
                  />
                );
              })}
              {!isLocked && (
                <ButtonBase
                  onClick={() => selectAllPermissionsImmediate(a, selected)}
                  style={{
                    marginTop: "8px",
                    padding: "6px 0",
                    fontSize: "11px",
                    color: "#FF6B35",
                    fontFamily: "Gilroy, sans-serif",
                    fontWeight: 700,
                    textAlign: "left",
                    width: "fit-content",
                  }}
                >
                  Give All Access
                </ButtonBase>
              )}
            </Box>
          );
        })()}
      </Popover>

      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        PaperProps={{
          sx: {
            background: "#1f2937",
            borderRadius: "16px",
            padding: "8px",
            minWidth: "340px",
          },
        }}
      >
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            style={{
              color: "white",
              fontSize: "18px",
              fontWeight: 700,
              fontFamily: "Gilroy, sans-serif",
            }}
          >
            {confirmDialog?.type === "delete" && "Delete Admin?"}
            {confirmDialog?.type === "revoke" && "Revoke Access?"}
            {confirmDialog?.type === "block" && "Block Admin?"}
            {confirmDialog?.type === "unblock" && "Unblock Admin?"}
          </Typography>
          <Typography
            style={{
              color: "#9ca3af",
              fontSize: "13px",
              fontFamily: "Gilroy, sans-serif",
              lineHeight: 1.6,
            }}
          >
            {confirmDialog?.type === "delete" && (
              <>
                This will permanently delete{" "}
                <b style={{ color: "white" }}>
                  {confirmDialog?.admin?.name || confirmDialog?.admin?.email}
                </b>
                . This cannot be undone.
              </>
            )}
            {confirmDialog?.type === "revoke" && (
              <>
                This will revoke{" "}
                <b style={{ color: "white" }}>
                  {confirmDialog?.admin?.name || confirmDialog?.admin?.email}
                </b>
                's admin access. You can restore it later.
              </>
            )}
            {confirmDialog?.type === "block" && (
              <>
                This will immediately block{" "}
                <b style={{ color: "white" }}>
                  {confirmDialog?.admin?.name || confirmDialog?.admin?.email}
                </b>{" "}
                from the admin panel.
              </>
            )}
            {confirmDialog?.type === "unblock" && (
              <>
                This will restore access for{" "}
                <b style={{ color: "white" }}>
                  {confirmDialog?.admin?.name || confirmDialog?.admin?.email}
                </b>
                .
              </>
            )}
          </Typography>
          <Box style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
            <ButtonBase
              onClick={() => setConfirmDialog(null)}
              style={{
                background: "#374151",
                color: "#9ca3af",
                padding: "8px 20px",
                borderRadius: "8px",
                fontFamily: "Gilroy, sans-serif",
                fontSize: "13px",
              }}
            >
              Cancel
            </ButtonBase>
            <ButtonBase
              onClick={confirmDialog?.onConfirm}
              style={{
                background:
                  confirmDialog?.type === "unblock"
                    ? "rgba(22,163,74,0.2)"
                    : "rgba(239,68,68,0.2)",
                color:
                  confirmDialog?.type === "unblock" ? "#4ade80" : "#f87171",
                padding: "8px 20px",
                borderRadius: "8px",
                fontFamily: "Gilroy, sans-serif",
                fontSize: "13px",
                fontWeight: 700,
              }}
            >
              {confirmDialog?.type === "delete" && "Yes, Delete"}
              {confirmDialog?.type === "revoke" && "Yes, Revoke"}
              {confirmDialog?.type === "block" && "Yes, Block"}
              {confirmDialog?.type === "unblock" && "Yes, Unblock"}
            </ButtonBase>
          </Box>
        </DialogContent>
      </Dialog>


      <Dialog
        open={createOpen}
        onClose={() => !createLoading && setCreateOpen(false)}
        PaperProps={{
          sx: {
            background: "#1f2937",
            borderRadius: "16px",
            padding: "8px",
            minWidth: "400px",
          },
        }}
      >
        <DialogContent
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <Typography
            style={{
              color: "white",
              fontSize: "18px",
              fontWeight: 700,
              fontFamily: "Gilroy, sans-serif",
            }}
          >
            Create Admin
          </Typography>

          <Box style={{ display: "flex", gap: "12px" }}>
            {[
              { label: "First Name", key: "first_name", placeholder: "John" },
              { label: "Last Name", key: "last_name", placeholder: "Smith" },
            ].map(({ label, key, placeholder }) => (
              <Box
                key={key}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <Typography
                  style={{
                    color: "#9ca3af",
                    fontSize: "12px",
                    fontFamily: "Gilroy, sans-serif",
                    fontWeight: 600,
                  }}
                >
                  {label}
                </Typography>
                <InputBase
                  type="text"
                  value={createForm[key]}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, [key]: e.target.value }))
                  }
                  placeholder={placeholder}
                  disabled={createLoading}
                  sx={{
                    background: "#111827",
                    borderRadius: "8px",
                    padding: "0 14px",
                    height: "40px",
                    color: "white",
                    fontFamily: "Gilroy, sans-serif",
                    fontSize: "13px",
                    border: "1px solid #374151",
                    "&:focus-within": { borderColor: "#FF6B35" },
                  }}
                />
              </Box>
            ))}
          </Box>

          {[
            {
              label: "Email",
              key: "email",
              type: "email",
              placeholder: "admin@example.com",
            },
            {
              label: "Password",
              key: "password",
              type: "password",
              placeholder: "Temporary password",
            },
          ].map(({ label, key, type, placeholder }) => (
            <Box
              key={key}
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <Typography
                style={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  fontFamily: "Gilroy, sans-serif",
                  fontWeight: 600,
                }}
              >
                {label}
              </Typography>
              <InputBase
                type={type}
                value={createForm[key]}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, [key]: e.target.value }))
                }
                placeholder={placeholder}
                disabled={createLoading}
                sx={{
                  background: "#111827",
                  borderRadius: "8px",
                  padding: "0 14px",
                  height: "40px",
                  color: "white",
                  fontFamily: "Gilroy, sans-serif",
                  fontSize: "13px",
                  border: "1px solid #374151",
                  "&:focus-within": { borderColor: "#FF6B35" },
                }}
              />
            </Box>
          ))}

          <Box style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography
              style={{
                color: "#9ca3af",
                fontSize: "12px",
                fontFamily: "Gilroy, sans-serif",
                fontWeight: 600,
              }}
            >
              Permissions
            </Typography>
            <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", background: "#111827", padding: "12px", borderRadius: "8px", border: "1px solid #374151" }}>
              {UI_PERMISSIONS.map((perm) => {
                const hasPerm = createForm.permissions?.includes(perm);
                return (
                  <FormControlLabel
                    key={perm}
                    control={
                      <Checkbox
                        size="small"
                        checked={hasPerm || false}
                        onChange={(e) => {
                          const newPerms = e.target.checked
                            ? [...(createForm.permissions || []), perm]
                            : (createForm.permissions || []).filter((p) => p !== perm);
                          setCreateForm((f) => ({ ...f, permissions: newPerms }));
                        }}
                        disabled={createLoading}
                        sx={{
                          color: "#4b5563",
                          padding: "2px 6px",
                          "&.Mui-checked": { color: "#FF6B35" },
                        }}
                      />
                    }
                    label={
                      <Typography style={{ fontSize: "12px", color: hasPerm ? "#FF6B35" : "#9ca3af", fontFamily: "Gilroy, sans-serif", fontWeight: hasPerm ? 600 : 400 }}>
                        {PERMISSION_LABELS[perm] || perm}
                      </Typography>
                    }
                    style={{ margin: 0 }}
                  />
                );
              })}
            </Box>
          </Box>

          {createError && (
            <Typography
              style={{
                color: "#f87171",
                fontSize: "12px",
                fontFamily: "Gilroy, sans-serif",
              }}
            >
              {createError}
            </Typography>
          )}

          <Box
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "4px",
            }}
          >
            <ButtonBase
              onClick={() => setCreateOpen(false)}
              disabled={createLoading}
              style={{
                background: "#374151",
                color: "#9ca3af",
                padding: "8px 20px",
                borderRadius: "8px",
                fontFamily: "Gilroy, sans-serif",
                fontSize: "13px",
              }}
            >
              Cancel
            </ButtonBase>
            <ButtonBase
              onClick={handleCreateAdmin}
              disabled={createLoading}
              style={{
                background: "#FF6B35",
                color: "white",
                padding: "8px 20px",
                borderRadius: "8px",
                fontFamily: "Gilroy, sans-serif",
                fontSize: "13px",
                fontWeight: 700,
              }}
            >
              {createLoading ? (
                <CircularProgress size={14} sx={{ color: "white" }} />
              ) : (
                "Create Admin"
              )}
            </ButtonBase>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const ActionBtn = ({ label, bg, color, onClick, disabled }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    style={{
      background: bg,
      color,
      padding: "4px 10px",
      borderRadius: "6px",
      fontSize: "11px",
      fontFamily: "Gilroy, sans-serif",
      fontWeight: 600,
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {label}
  </ButtonBase>
);

export default AdminManagement;
